import { resolveApiUrl } from './apiBase';

export class ApiError extends Error {
  constructor(message, { status = 0, code = '', details = null } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

async function parseResponse(response) {
  if (response.status === 204) return null;
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) return response.json();
  const text = await response.text();
  return text || null;
}

function detailMessage(payload, fallback) {
  if (typeof payload === 'string' && payload) return payload;
  if (typeof payload?.detail === 'string') return payload.detail;
  if (typeof payload?.message === 'string') return payload.message;
  if (Array.isArray(payload?.detail)) {
    return payload.detail.map((item) => item.msg || String(item)).join('. ');
  }
  return fallback;
}

export function createApiClient({ getToken, onUnauthorized, fetchImpl = fetch } = {}) {
  async function request(path, {
    method = 'GET',
    query,
    body,
    signal,
    headers: providedHeaders,
    preserveSessionOnUnauthorized = false,
  } = {}) {
    const headers = new Headers(providedHeaders || {});
    const token = getToken?.();
    if (token) headers.set('Authorization', `Bearer ${token}`);

    let requestBody = body;
    const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
    if (body !== undefined && body !== null && !isFormData) {
      headers.set('Content-Type', 'application/json');
      requestBody = JSON.stringify(body);
    }

    let response;
    try {
      response = await fetchImpl(resolveApiUrl(path, query), {
        method,
        headers,
        body: requestBody,
        signal,
      });
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      throw new ApiError('Unable to reach the Operations service. Check your connection and try again.', {
        code: 'network_error',
        details: error,
      });
    }

    const payload = await parseResponse(response);
    if (!response.ok) {
      if (response.status === 401 && !preserveSessionOnUnauthorized) onUnauthorized?.();
      throw new ApiError(detailMessage(payload, `Request failed (${response.status})`), {
        status: response.status,
        code: payload?.code || payload?.error || '',
        details: payload,
      });
    }
    return payload;
  }

  return {
    request,
    get: (path, options = {}) => request(path, options),
    post: (path, body, options = {}) => request(path, { ...options, method: 'POST', body }),
    patch: (path, body, options = {}) => request(path, { ...options, method: 'PATCH', body }),
    delete: (path, options = {}) => request(path, { ...options, method: 'DELETE' }),
  };
}

export function errorMessage(error, fallback = 'Something went wrong. Please try again.') {
  return error instanceof ApiError && error.message ? error.message : fallback;
}
